<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
  >
    <div :class="$style.container">
      <div
        v-if="data?.fields?.media?.fields?.file?.url"
        :class="$style.mediaImage"
      >
        <AspectRatio
          :ratio="
            data?.fields?.media?.fields?.file?.details?.image?.width +
            'x' +
            data?.fields?.media?.fields?.file?.details?.image?.height
          "
          as="picture"
          inline-block
        >
          <BasePicture
            :src="data?.fields?.media?.fields?.file?.url + '?q=70'"
            alt=""
            :img-class="$style.img"
          />
        </AspectRatio>
      </div>
      <div :class="$style.content">
        <header v-if="data?.fields?.header || data?.fields?.subheader">
          <BaseHeading size="h2" as="h3">
            {{ data?.fields?.header }}
          </BaseHeading>
          <MarkedParser
            :class="$style.subheader"
            :md="data?.fields?.subheader"
          />
        </header>
        <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />
        <div v-if="data?.fields?.ctas?.length">
          <CallToAction
            v-for="c in data?.fields?.ctas"
            :key="c?.sys.id"
            :data-ctf-id="c?.sys.id"
            :cta="c"
          />
        </div>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 40px 20px;
  --wrap-padding--md: 54px 20px;
  --wrap-padding: var(--wrap-padding--base);

  --container-max-width--base: 520px;
  --container-max-width--md: 1220px;
  --container-max-width: var(--container-max-width--base);

  --container-text-align--base: center;
  --container-text-align--md: left;
  --container-text-align: var(--container-text-align--base);

  --container-gap--base: 2em;
  --container-gap--md: 4em;
  --container-gap: var(--container-gap--base);

  --container-grid-template-columns--base: 1fr;
  --container-grid-template-columns--md: var(--mediaImage-max-width) 1fr;
  --container-grid-template-columns: var(
    --container-grid-template-columns--base
  );

  --mediaImage-max-width: 520px;

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
    --container-max-width: var(--container-max-width--md);
    --container-text-align: var(--container-text-align--md);
    --container-gap: var(--container-gap--md);
    --container-grid-template-columns: var(
      --container-grid-template-columns--md
    );
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.container {
  display: grid;
  grid-template-columns: var(--container-grid-template-columns);
  gap: var(--container-gap);
  align-items: center;

  max-width: var(--container-max-width);
  margin: 0 auto;

  text-align: var(--container-text-align);
}

.content {
  display: grid;
  gap: 18px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
  }

  h1,
  h2 {
    @include fluid-type(26px, 42px);
  }
}

.copy {
  @include fluid-type(16px, 19px);
}

.mediaImage {
  max-width: var(--mediaImage-max-width);
}
.img {
  max-width: 100%;
  object-fit: contain;
}
</style>
